// apps/admin/src/pages/admin/MarketingStrategyReport.js
// Dynamic Marketing Strategy Command Center, Multilingual Scripts & Optimization Tools
// Built for Super Admin from docs/marketing_strategy.md

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MARKET_STRATEGY_DATA,
} from "../../data/marketingStrategyData";
import {
  getStrategyAnalysis,
  reanalyzeStrategy,
  toggleChecklistItem,
} from "../../services/marketing";
import "./MarketingStrategyReport.css";

export default function MarketingStrategyReport({ notify }) {
  // Sub-navigation view
  const [activeTab, setActiveTab] = useState("strategy"); // 'strategy' | 'budget10k' | 'linking' | 'scripts' | 'simulator' | 'recommendations' | 'cadence' | 'objections'

  // Dynamic analysis & checklist state
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [checklist, setChecklist] = useState({});

  // Traffic Linking Map search state
  const [linkingSearch, setLinkingSearch] = useState("");

  // Multilingual scripts state
  const [selectedLang, setSelectedLang] = useState("en");
  const [selectedScriptType, setSelectedScriptType] = useState("all"); // 'all' | 'generic' | 'niche'

  // Simulator optimization tool state
  const [simDailyBudget, setSimDailyBudget] = useState(1500); // in INR
  const [simNiche, setSimNiche] = useState("blouses");
  const [simGeo, setSimGeo] = useState("south");

  // AI Ad Hook generator state
  const [aiPlatform, setAiPlatform] = useState("reels");
  const [aiHookType, setAiHookType] = useState("pain"); // 'pain' | 'zeroComm' | 'fastReady'
  const [aiLang, setAiLang] = useState("en");
  const [generatedHook, setGeneratedHook] = useState("");

  // WhatsApp Business Automation state
  const [cadenceSubTab, setCadenceSubTab] = useState("autoReplies"); // 'autoReplies' | 'onboarding'
  const [waScriptCategory, setWaScriptCategory] = useState("all");

  // Load live analysis from backend or fallback to defaults
  const loadAnalysis = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getStrategyAnalysis();
      if (res?.data?.success && res.data.data) {
        setAnalysisData(res.data.data);
        if (res.data.data.checklist) {
          setChecklist(res.data.data.checklist);
        }
      }
    } catch (err) {
      // Fallback gracefully
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  // Re-analyze Marketing Strategy
  const handleReanalyze = async () => {
    try {
      setReanalyzing(true);
      const res = await reanalyzeStrategy();
      if (res?.data?.success) {
        notify(res.data.message || "Marketing Strategy re-analyzed successfully!", "success");
        await loadAnalysis();
      }
    } catch (err) {
      notify("Failed to re-analyze marketing strategy", "error");
    } finally {
      setReanalyzing(false);
    }
  };

  // Toggle checklist item
  const handleToggleChecklist = async (itemKey, currentStatus) => {
    const nextStatus = !currentStatus;
    setChecklist((prev) => ({ ...prev, [itemKey]: nextStatus }));
    try {
      await toggleChecklistItem(itemKey, nextStatus);
    } catch (err) {
      // Local state already updated
    }
  };

  // 1-Click Copy helper
  const copyToClipboard = async (text, successMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(successMsg || "Copied to clipboard!", "success");
    } catch {
      notify("Unable to copy to clipboard", "error");
    }
  };

  // Download Full Markdown Strategy
  const handleExportMarkdown = () => {
    const b = MARKET_STRATEGY_DATA.budgetPlan10k;
    const content = `# Ownezzy Master Marketing Strategy & Sales Playbook
Generated from Super Admin Command Center
Last Readiness Score: ${analysisData?.healthScore || 94}/100

## 1. Executive Summary & Core Positioning
- **Target TAM:** ${MARKET_STRATEGY_DATA.overview.tam} (${MARKET_STRATEGY_DATA.overview.tamDescription})
- **Positioning:** ${MARKET_STRATEGY_DATA.overview.positioning}
- **Winning USP:** "${MARKET_STRATEGY_DATA.overview.usp}"

## 2. Master ₹10,000 / Month Advertising Budget Plan
- **Total Monthly Spend:** ₹${b.monthlyBudget.toLocaleString("en-IN")} (~₹${b.dailySpend}/day)
- **Projected Paid Conversions:** ${b.funnelProjections.paidConversions}
- **Projected CAC:** ${b.funnelProjections.cac}
- **Projected MRR Added:** ${b.funnelProjections.monthlyMrrAdded}
- **Payback Period:** ${b.funnelProjections.paybackPeriod}
- **Estimated ROAS:** ${b.funnelProjections.sixMonthRoas}

### Channel Breakdown:
${b.channels.map((c) => `- **${c.channel}** (${c.pct}% | ₹${c.monthlyAllocation.toLocaleString("en-IN")}/mo | ~₹${c.dailySpend}/day): ${c.targetCampaigns} -> ${c.notes}`).join("\n")}

### 4-Week Execution Schedule:
${b.weeklyCalendar.map((w) => `- **${w.week}** (${w.budget}): ${w.focus} — ${w.tasks}`).join("\n")}

## 3. Deep Linking & Traffic Routing Map
${MARKET_STRATEGY_DATA.linkingMap.map((l) => `- **${l.angle}** [${l.target}] -> ${l.destUrl}${l.utm} (Fallback: ${l.fallback}) | Hook: "${l.hook}" | Trigger: ${l.modalTrigger}`).join("\n")}

## 4. Multilingual Video Ad Scripts
${MARKET_STRATEGY_DATA.languages.map((lang) => {
  const scripts = MARKET_STRATEGY_DATA.adScripts[lang.id] || [];
  return `### Language: ${lang.name} (${lang.flag})\n` +
    scripts.map((s) => `#### [${s.type.toUpperCase()}] ${s.title}\nTarget: ${s.target}\nDestination: ${s.destination}\nDuration: ${s.duration}\n\n` +
      s.scenes.map((sc) => `- **${sc.time}** | Visual: ${sc.visual} | Voiceover: "${sc.voiceover}" | Text Overlay: [${sc.textOverlay}]`).join("\n")
    ).join("\n\n");
}).join("\n\n")}

## 5. Automated WhatsApp Sales Cadence
${MARKET_STRATEGY_DATA.salesCadence.map((c) => `### ${c.stage}: ${c.title} (Trigger: ${c.trigger})\n\`\`\`\n${c.template}\n\`\`\``).join("\n\n")}

## 6. Objection Handling Matrix
${MARKET_STRATEGY_DATA.objections.map((o) => `**Q:** "${o.objection}"\n**A:** ${o.response}`).join("\n\n")}
`;
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ownezzy-marketing-strategy-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    notify("Marketing Strategy Report exported as Markdown!", "success");
  };

  // Filtered linking map based on search query
  const filteredLinkingMap = useMemo(() => {
    const map = MARKET_STRATEGY_DATA.linkingMap || [];
    if (!linkingSearch.trim()) return map;
    const q = linkingSearch.toLowerCase();
    return map.filter(
      (item) =>
        item.angle.toLowerCase().includes(q) ||
        item.target.toLowerCase().includes(q) ||
        item.destUrl.toLowerCase().includes(q) ||
        item.hook.toLowerCase().includes(q) ||
        (item.storeName && item.storeName.toLowerCase().includes(q))
    );
  }, [linkingSearch]);

  // Simulator dynamic calculations
  const simResults = useMemo(() => {
    const monthlyBudget = simDailyBudget * 30;
    // Estimated CPM based on geography
    const cpm = simGeo === "south" ? 110 : simGeo === "metro" ? 140 : 95;
    const monthlyImpressions = Math.round((monthlyBudget / cpm) * 1000);
    const dailyImpressions = Math.round(monthlyImpressions / 30);

    // CTR by niche across all 20 demo stores + generic
    const ctrMap = {
      clothing: 0.034,
      plants: 0.028,
      gifts: 0.032,
      pets: 0.030,
      aquarium: 0.027,
      wellness: 0.031,
      footwear: 0.036,
      uruli: 0.033,
      grocery: 0.026,
      fresh: 0.025,
      tech: 0.042,
      cases: 0.042,
      bags: 0.035,
      crafts: 0.037,
      decor: 0.032,
      blouses: 0.039,
      favors: 0.034,
      resin: 0.041,
      crochet: 0.038,
      pattu: 0.036,
      linens: 0.033,
      generic: 0.031
    };
    const ctr = ctrMap[simNiche] || 0.034;
    const dailyClicks = Math.round(dailyImpressions * ctr);
    const monthlyClicks = dailyClicks * 30;
    const avgCpc = (monthlyBudget / monthlyClicks).toFixed(1);

    // 10s auto-modal trigger rate on demo sites (32% of visitors browse for >10s)
    const monthlyModalOpens = Math.round(monthlyClicks * 0.32);

    // Free trial signups (18% of modal opens)
    const monthlyTrials = Math.round(monthlyModalOpens * 0.18);
    const dailyTrials = (monthlyTrials / 30).toFixed(1);

    // Paid subscriber conversion (18.4% of trials convert after 7 days)
    const monthlyPaidStores = Math.round(monthlyTrials * 0.184);

    // CAC calculation
    const estimatedCac = monthlyPaidStores > 0 ? Math.round(monthlyBudget / monthlyPaidStores) : 180;

    // Projected Monthly Recurring Revenue (Avg plan ₹449/mo)
    const projectedMrr = monthlyPaidStores * 449;

    // Projected 6-month LTV based on retention
    const projectedLtvRevenue = monthlyPaidStores * 449 * 5;
    const roas = (projectedLtvRevenue / monthlyBudget).toFixed(1);

    return {
      monthlyBudget,
      dailyImpressions,
      dailyClicks,
      avgCpc,
      dailyTrials,
      monthlyTrials,
      monthlyPaidStores,
      estimatedCac,
      projectedMrr,
      roas
    };
  }, [simDailyBudget, simNiche, simGeo]);

  // AI Hook Generator
  const generateAdHook = () => {
    const hooks = {
      pain: {
        en: `❌ Tired of typing "Price please" and "Check DM" 100 times every single day on Instagram?\n\nHalf your customers ghost before sending UPI payment screenshots. Meet Ownezzy! Launch your own automated online store in 60 seconds with direct UPI payments, instant WhatsApp alerts, and 0% sales commission.\n\n🎁 No coding. No credit card needed. Start your 7-day 100% Free Trial today! 👇\n#smallbusinessindia #instagramsellers #ownezzy #boutiquebusiness`,
        hi: `❌ क्या आप भी रोज़ इंस्टाग्राम पर 'Price Please' और 'Check DM' लिखते-लिखते थक चुके हैं?\n\nआधे कस्टमर्स तो QR कोड मांगने के बाद गायब हो जाते हैं! अब अपनाइए Ownezzy! सिर्फ 60 सेकंड में अपनी रेडीमेड ऑनलाइन दुकान शुरू करें। डायरेक्ट UPI पेमेंट्स, WhatsApp आर्डर अलर्ट्स, और ZERO कमीशन!\n\n🎁 14 दिन बिल्कुल फ्री इस्तेमाल करें—कोई क्रेडिट कार्ड नहीं चाहिए! 👇\n#businessindia #instaseller #onlineshop #ownezzy`,
        ta: `❌ Instagram-la business pandreengala? Innum daily 'Price please' nu DM-la type panni tired aagiteengala?\n\nCustomer GPay number கேட்டுட்டு பேமெண்ட் பண்ணாம போயிடுறாங்களா? 60 நொடியில் உங்கள் பிராண்டிற்கு ஒரு புரொபஷனல் வெப்சைட்! நேரடி UPI பேமெண்ட் மற்றும் ஜீரோ கமிஷன்!\n\n🎁 14 நாட்கள் இலவச டிரையல் — உடனே ஸ்டார்ட் பண்ணுங்க! 👇\n#tamilnaduboutique #aariboutique #ownezzy #chennaisellers`
      },
      zeroComm: {
        en: `💰 Stop paying 15% to 30% commission to marketplaces on every single order!\n\nWith Ownezzy, customer payments go straight to your personal PhonePe, Google Pay, or Paytm account with 0% platform commission. You keep 100% of your hard-earned profits.\n\n⚡ Pre-built niche stores ready in 60s. Start your 7-day free trial now! 👇\n#zerocommission #ecommerceindia #d2cindia #ownezzy`,
        hi: `💸 मार्केटप्लेस को हर आर्डर पर 15% से 30% कमीशन देना बंद कीजिए!\n\nOwnezzy पर सारा पैसा सीधा आपके बैंक अकाउंट / UPI में आएगा और कोई कमीशन नहीं कटेगा। आपकी मेहनत, आपका 100% प्रॉफिट!\n\n🚀 60 सेकंड में अपनी वेबसाइट शुरू करें। 14 दिन फ्री ट्रायल! 👇\n#zerocommission #makeinindia #ownezzy`,
        ta: `💸 ஒவ்வொரு ஆர்டருக்கும் 15% முதல் 30% வரை கமிஷன் கொடுப்பதை நிறுத்துங்கள்!\n\nOwnezzy-ல் வாடிக்கையாளர் பணம் நேரடியாக உங்கள் PhonePe / GPay அக்கவுண்ட்டிற்கு வரும். 1 பைசா கூட கமிஷன் கிடையாது!\n\n✨ 60 நொடியில் உங்கள் ஸ்டோர் தயார். 14 நாட்கள் இலவச டிரையல்! 👇\n#zerocommission #tamilbusiness #ownezzy`
      },
      fastReady: {
        en: `⚡ Don't spend ₹50,000 and 2 months waiting for a web developer!\n\nOwnezzy gives you a ready-made online boutique or retail website in just 60 seconds. Pre-loaded with 30+ products, banners, and instant UPI checkout.\n\n🌸 Test it 100% free for 7 days — no credit card needed! Tap below! 👇\n#quickstore #websitebuilder #ownezzy #instabusiness`,
        hi: `🚀 वेबसाइट बनाने के लिए ₹50,000 खर्च करने या डेवलपर के पीछे भागने की ज़रूरत नहीं!\n\nOwnezzy पर पाइए अपनी रेडीमेड बुटीक वेबसाइट सिर्फ 60 सेकंड में—प्रोडक्ट्स, बैनर्स और UPI चेकआउट के साथ पहले से ही तैयार।\n\n🌟 14 दिनों तक बिल्कुल फ्री इस्तेमाल करें! 👇\n#digitalindia #ownezzy #shopwebsite`,
        ta: `🚀 வெப்சைட் செய்ய ₹50,000 செலவு பண்ண தேவையில்லை!\n\nOwnezzy-ல் உங்கள் ஆரி ஒர்க் / புட்டிக் பிசினஸிற்கு 60 நொடியில் ரெடிமேட் வெப்சைட் தயார். ப்ராடக்ட்ஸ், பேனர்கள் மற்றும் நேரடி UPI செக்அவுட் உடன்!\n\n✨ 14 நாட்கள் 100% இலவச டிரையல்! இப்போதே தொடங்குங்கள்! 👇\n#aariblouse #onlineboutique #ownezzy`
      }
    };
    const hookText = hooks[aiHookType]?.[aiLang] || hooks[aiHookType]?.en;
    setGeneratedHook(hookText);
  };

  useEffect(() => {
    generateAdHook();
  }, [aiPlatform, aiHookType, aiLang]);

  // Active scripts for selected language
  const activeScripts = useMemo(() => {
    const list = MARKET_STRATEGY_DATA.adScripts[selectedLang] || [];
    if (selectedScriptType === "all") return list;
    if (selectedScriptType === "generic") return list.filter((s) => s.type === "generic");
    if (selectedScriptType === "niche") return list.filter((s) => s.type === "niche");
    return list.filter((s) => s.niche === selectedScriptType);
  }, [selectedLang, selectedScriptType]);

  // Checklist completion stats
  const checklistStats = useMemo(() => {
    const total = MARKET_STRATEGY_DATA.actionChecklist.length;
    let completed = 0;
    MARKET_STRATEGY_DATA.actionChecklist.forEach((item) => {
      const isDone = checklist[item.id] !== undefined ? checklist[item.id] : item.completed;
      if (isDone) completed++;
    });
    return {
      total,
      completed,
      pct: Math.round((completed / total) * 100)
    };
  }, [checklist]);

  return (
    <div className="mkt-report-container">
      {/* MASTER STRATEGY HEADER */}
      <header className="mkt-header-card">
        <div className="mkt-header-left">
          <div className="mkt-badge-row">
            <span className="mkt-tag-pill">🚀 Super Admin Marketing Command Center</span>
            <span className="mkt-score-pill">
              Readiness Score: {analysisData?.healthScore || 94}/100
            </span>
            <span style={{ fontSize: "11px", color: "#a7f3d0" }}>
              Last Analyzed: {analysisData?.lastAnalyzedAt ? new Date(analysisData.lastAnalyzedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : "Live Today"}
            </span>
          </div>
          <h1 className="mkt-header-title">
            Master Marketing Strategy, Multilingual Video Ads &amp; Sales Playbook
          </h1>
          <p className="mkt-header-desc">
            Direct blueprint to capture India's 15 Million+ Instagram, WhatsApp &amp; Facebook micro-sellers with automated niche funnels, 5-language regional video scripts, and zero-commission positioning.
          </p>
        </div>

        <div className="mkt-header-actions">
          <button
            type="button"
            className="btn-reanalyze-mkt"
            onClick={handleReanalyze}
            disabled={reanalyzing}
            title="Re-run marketing strategy health scan and recalculate CAC/ROAS projections"
          >
            <span className={reanalyzing ? "spin-icon" : ""}>🔄</span>
            <span>{reanalyzing ? "Re-analyzing Strategy..." : "Re-analyse Strategy"}</span>
          </button>

          <button
            type="button"
            className="btn-export-mkt"
            onClick={handleExportMarkdown}
            title="Download complete marketing report as Markdown"
          >
            <span>📥 Export Playbook (.md)</span>
          </button>
        </div>
      </header>

      {/* QUICK KPI RIBBON */}
      <div className="mkt-kpi-grid">
        <div className="mkt-kpi-card">
          <span className="mkt-kpi-label">Target Seller TAM</span>
          <span className="mkt-kpi-value">{MARKET_STRATEGY_DATA.overview.tam}</span>
          <span className="mkt-kpi-sub">Micro-sellers &amp; Social Brands</span>
        </div>

        <div className="mkt-kpi-card">
          <span className="mkt-kpi-label">Active Live Stores</span>
          <span className="mkt-kpi-value">{analysisData?.metrics?.activeLiveStores || 14}</span>
          <span className="mkt-kpi-sub">Hosting on Ownezzy</span>
        </div>

        <div className="mkt-kpi-card">
          <span className="mkt-kpi-label">Projected CAC</span>
          <span className="mkt-kpi-value">₹165</span>
          <span className="mkt-kpi-sub">Per Paid Store Conversion</span>
        </div>

        <div className="mkt-kpi-card">
          <span className="mkt-kpi-label">Trial Conversion Rate</span>
          <span className="mkt-kpi-value">18.4%</span>
          <span className="mkt-kpi-sub">7-Day Free Trial to Paid</span>
        </div>

        <div className="mkt-kpi-card">
          <span className="mkt-kpi-label">Projected Blended ROAS</span>
          <span className="mkt-kpi-value">3.4x</span>
          <span className="mkt-kpi-sub">Based on 6-Month LTV</span>
        </div>
      </div>

      {/* SUB-NAVIGATION TABS */}
      <nav className="mkt-nav-tabs" aria-label="Marketing Strategy Sections">
        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "strategy" ? "active" : ""}`}
          onClick={() => setActiveTab("strategy")}
        >
          <span>📊 Master Strategy &amp; Positioning</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "budget10k" ? "active" : ""}`}
          onClick={() => setActiveTab("budget10k")}
        >
          <span>💰 ₹10,000/Mo Budget Plan</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "linking" ? "active" : ""}`}
          onClick={() => setActiveTab("linking")}
        >
          <span>🔗 Traffic Linking Map</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "scripts" ? "active" : ""}`}
          onClick={() => setActiveTab("scripts")}
        >
          <span>🎬 Video Ad Scripts ({MARKET_STRATEGY_DATA.languages.length} Languages)</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "simulator" ? "active" : ""}`}
          onClick={() => setActiveTab("simulator")}
        >
          <span>🎯 Meta Ads Budget &amp; ROAS Simulator</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "recommendations" ? "active" : ""}`}
          onClick={() => setActiveTab("recommendations")}
        >
          <span>💡 AI Recommendations ({checklistStats.completed}/{checklistStats.total})</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "cadence" ? "active" : ""}`}
          onClick={() => setActiveTab("cadence")}
        >
          <span>⚡ Automated WhatsApp Sales Cadence</span>
        </button>

        <button
          type="button"
          className={`mkt-nav-btn ${activeTab === "objections" ? "active" : ""}`}
          onClick={() => setActiveTab("objections")}
        >
          <span>🛡️ Objection Handling &amp; Closing</span>
        </button>
      </nav>

      {/* TAB 1: MASTER STRATEGY & POSITIONING */}
      {activeTab === "strategy" && (
        <section className="mkt-section-card" aria-label="Executive Strategy">
          <div className="mkt-section-header">
            <div>
              <h3 className="mkt-section-title">Market Opportunity &amp; Core Positioning</h3>
              <p className="mkt-section-desc">
                High-intent, high-margin sellers across South India and Tier-2 industrial hubs transitioning from chaotic Instagram DMs to automated stores.
              </p>
            </div>
            <button
              type="button"
              className="btn-copy-script"
              onClick={() => copyToClipboard(MARKET_STRATEGY_DATA.overview.usp, "USP copied!")}
            >
              📋 Copy Core USP
            </button>
          </div>

          <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "14px 18px" }}>
            <strong style={{ display: "block", color: "#065f46", fontSize: "14px", marginBottom: "4px" }}>
              💎 Ownezzy Winning USP:
            </strong>
            <p style={{ margin: 0, fontSize: "13.5px", color: "#0f172a", fontWeight: 600 }}>
              "{MARKET_STRATEGY_DATA.overview.usp}"
            </p>
          </div>

          <div>
            <h4 style={{ margin: "0 0 10px", fontSize: "14px", fontWeight: 800 }}>
              The 4 Irresistible Growth Pillars
            </h4>
            <div className="pillars-grid">
              {MARKET_STRATEGY_DATA.overview.pillars.map((pillar, idx) => (
                <div key={idx} className="pillar-card">
                  <span className="pillar-tag">{pillar.tagline}</span>
                  <h5 className="pillar-title">{pillar.title}</h5>
                  <p className="pillar-desc">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 style={{ margin: "14px 0 10px", fontSize: "14px", fontWeight: 800 }}>
              Psychological Context: Current Nightmare vs. Ownezzy Emotional Relief
            </h4>
            <div className="triggers-table-wrap">
              <table className="triggers-table">
                <thead>
                  <tr>
                    <th style={{ width: "50%" }}>Current Seller Nightmare (Pain Trigger)</th>
                    <th style={{ width: "50%" }}>The Ownezzy Reality (Emotional Relief)</th>
                  </tr>
                </thead>
                <tbody>
                  {MARKET_STRATEGY_DATA.overview.psychologicalTriggers.map((trig, i) => (
                    <tr key={i}>
                      <td className="trigger-pain">❌ {trig.nightmare}</td>
                      <td className="trigger-relief">✨ {trig.reality}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 style={{ margin: "14px 0 10px", fontSize: "14px", fontWeight: 800 }}>
              Top Priority Regional Hubs &amp; Product Niches
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
              {MARKET_STRATEGY_DATA.overview.keyHubs.map((hub, idx) => (
                <div key={idx} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 14px" }}>
                  <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>📍 {hub.name}</strong>
                  <div style={{ fontSize: "11.5px", color: "#64748b", margin: "4px 0" }}>{hub.cities}</div>
                  <div style={{ fontSize: "12px", color: "#047857", fontWeight: 700 }}>Focus: {hub.focus}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TAB 2: ₹10,000 / MONTH ADVERTISING BUDGET PLAN */}
      {activeTab === "budget10k" && (
        <section className="mkt-section-card" aria-label="10000 Monthly Budget Plan">
          <div className="mkt-section-header">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                <span className="script-badge-type">🎯 High-ROI Capital Allocation</span>
                <span style={{ fontSize: "12px", color: "#059669", fontWeight: 700 }}>
                  Cash-flow Positive in &lt; 28 Days
                </span>
              </div>
              <h3 className="mkt-section-title">
                Master ₹10,000 / Month Advertising Budget Allocation
              </h3>
              <p className="mkt-section-desc">
                Disciplined daily allocation across Meta Ads (70%), Google Search (20%), and automated WhatsApp Retargeting (10%) with verified CAC and payback projections.
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-copy-script"
                onClick={() => {
                  setSimDailyBudget(333);
                  setActiveTab("simulator");
                  notify("Loaded ₹10k plan (~₹333/day) into simulator!", "success");
                }}
              >
                ⚡ Test in Simulator
              </button>
              <button
                type="button"
                className="btn-copy-script"
                onClick={() => {
                  const b = MARKET_STRATEGY_DATA.budgetPlan10k;
                  const summaryText = `Ownezzy ₹10,000/Month Advertising Plan:\n- Meta Ads (70%): ₹7,000/mo (~₹233/day) -> Instagram Reels\n- Google Search (20%): ₹2,000/mo (~₹66/day) -> High-intent keywords\n- WhatsApp Retargeting (10%): ₹1,000/mo (~₹33/day) -> Onboarding Cadence\n\nDeliverables:\n- Impressions: ${b.funnelProjections.impressions}\n- Clicks: ${b.funnelProjections.clicks}\n- Free Trials: ${b.funnelProjections.trialsCreated}\n- Paid Conversions: ${b.funnelProjections.paidConversions}\n- CAC: ${b.funnelProjections.cac}\n- Projected MRR: ${b.funnelProjections.monthlyMrrAdded}\n- Payback: ${b.funnelProjections.paybackPeriod}`;
                  copyToClipboard(summaryText, "₹10,000 Budget Plan copied!");
                }}
              >
                📋 Copy Budget Plan Summary
              </button>
            </div>
          </div>

          {/* Allocation Highlights Banner */}
          <div className="budget-banner">
            <div className="budget-banner-metric">
              <span className="budget-metric-lbl">Total Monthly Ad Budget</span>
              <span className="budget-metric-val">₹{MARKET_STRATEGY_DATA.budgetPlan10k.monthlyBudget.toLocaleString("en-IN")}</span>
              <span className="budget-metric-sub">~₹{MARKET_STRATEGY_DATA.budgetPlan10k.dailySpend}/day spend cap</span>
            </div>
            <div className="budget-banner-metric">
              <span className="budget-metric-lbl">Projected Paid Subscribers</span>
              <span className="budget-metric-val">{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.paidConversions}</span>
              <span className="budget-metric-sub">From {MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.trialsCreated}</span>
            </div>
            <div className="budget-banner-metric">
              <span className="budget-metric-lbl">Projected CAC</span>
              <span className="budget-metric-val">{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.cac}</span>
              <span className="budget-metric-sub">Per paid merchant</span>
            </div>
            <div className="budget-banner-metric">
              <span className="budget-metric-lbl">New Added MRR</span>
              <span className="budget-metric-val" style={{ color: "#34d399" }}>{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.monthlyMrrAdded}</span>
              <span className="budget-metric-sub">Payback in &lt; 28 days</span>
            </div>
          </div>

          {/* 3 Channel Breakdown Cards */}
          <div>
            <h4 style={{ margin: "16px 0 10px", fontSize: "14.5px", fontWeight: 800 }}>
              1. Three-Channel Allocation Breakdown
            </h4>
            <div className="budget-channels-grid">
              {MARKET_STRATEGY_DATA.budgetPlan10k.channels.map((ch, idx) => (
                <div key={idx} className="budget-channel-card">
                  <div className="budget-channel-head">
                    <div>
                      <span className="budget-channel-pct">{ch.pct}% Budget</span>
                      <h5 className="budget-channel-title">{ch.channel}</h5>
                    </div>
                    <div className="budget-channel-amount">
                      ₹{ch.monthlyAllocation.toLocaleString("en-IN")}/mo
                      <small>~₹{ch.dailySpend}/day</small>
                    </div>
                  </div>

                  <div className="budget-channel-meta">
                    <div><strong>Format:</strong> {ch.format}</div>
                    <div style={{ marginTop: "4px" }}><strong>Target Campaigns:</strong> {ch.targetCampaigns}</div>
                    <div style={{ marginTop: "4px", color: "#047857" }}><strong>Execution:</strong> {ch.notes}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Funnel Deliverables Grid */}
          <div>
            <h4 style={{ margin: "18px 0 10px", fontSize: "14.5px", fontWeight: 800 }}>
              2. Projected Monthly Funnel Deliverables (₹10,000 Budget)
            </h4>
            <div className="budget-funnel-grid">
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">Video Ad Impressions</span>
                <span className="budget-funnel-val">{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.impressions}</span>
                <span className="budget-funnel-sub">Instagram Reels &amp; Google Search</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">High-Intent Clicks</span>
                <span className="budget-funnel-val">{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.clicks}</span>
                <span className="budget-funnel-sub">CPC: {MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.cpc}</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">10s Auto-Modal Opens</span>
                <span className="budget-funnel-val">{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.modalEngagements}</span>
                <span className="budget-funnel-sub">30% demo storefront engagement</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">7-Day Free Trials Created</span>
                <span className="budget-funnel-val" style={{ color: "#059669" }}>{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.trialsCreated}</span>
                <span className="budget-funnel-sub">Trial Cost: {MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.trialCost}</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">Converted Paid Stores (18.4%)</span>
                <span className="budget-funnel-val" style={{ color: "#047857" }}>{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.paidConversions}</span>
                <span className="budget-funnel-sub">Average plan ₹449/mo</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">Added Monthly Recurring Revenue</span>
                <span className="budget-funnel-val" style={{ color: "#10b981" }}>{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.monthlyMrrAdded}</span>
                <span className="budget-funnel-sub">MRR added every single month</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">Payback Period</span>
                <span className="budget-funnel-val" style={{ color: "#0284c7" }}>{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.paybackPeriod}</span>
                <span className="budget-funnel-sub">Self-funding loop</span>
              </div>
              <div className="budget-funnel-card">
                <span className="budget-funnel-label">6-Month Blended ROAS</span>
                <span className="budget-funnel-val" style={{ color: "#7c3aed" }}>{MARKET_STRATEGY_DATA.budgetPlan10k.funnelProjections.sixMonthRoas}</span>
                <span className="budget-funnel-sub">High seller retention LTV</span>
              </div>
            </div>
          </div>

          {/* Week-by-Week Execution Timeline */}
          <div>
            <h4 style={{ margin: "18px 0 10px", fontSize: "14.5px", fontWeight: 800 }}>
              3. 4-Week Execution Calendar (₹2,500 / Week Sprint)
            </h4>
            <div className="weekly-timeline-grid">
              {MARKET_STRATEGY_DATA.budgetPlan10k.weeklyCalendar.map((wk, idx) => (
                <div key={idx} className="weekly-card">
                  <div className="weekly-head">
                    <span className="weekly-badge">{wk.week}</span>
                    <strong style={{ fontSize: "13px", color: "#065f46" }}>Budget: {wk.budget}</strong>
                  </div>
                  <h5 className="weekly-focus">🎯 {wk.focus}</h5>
                  <p className="weekly-tasks">{wk.tasks}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TAB 3: TRAFFIC LINKING MAP */}
      {activeTab === "linking" && (
        <section className="mkt-section-card" aria-label="Deep Linking Map">
          <div className="mkt-section-header">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                <span className="script-badge-type">🔗 Conversion Routing Architecture</span>
                <span style={{ fontSize: "12px", color: "#059669", fontWeight: 700 }}>
                  3x Higher Conversion vs Generic Homepage
                </span>
              </div>
              <h3 className="mkt-section-title">
                Where to Do the Linkings (Deep Linking &amp; Traffic Routing Map)
              </h3>
              <p className="mkt-section-desc">
                Never direct cold traffic to a generic homepage. Directing users straight into niche demo storefronts with pre-loaded products and a 10-second auto-modal converts 3x higher.
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                placeholder="🔍 Search niche, angle, or URL..."
                value={linkingSearch}
                onChange={(e) => setLinkingSearch(e.target.value)}
                className="linking-search-input"
              />
            </div>
          </div>

          {/* Quick Demo Store Testing Strip */}
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "12px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", flexWrap: "wrap", gap: "6px" }}>
              <strong style={{ fontSize: "13px", color: "#166534" }}>
                ⚡ All 20 Live Demo Storefronts (Click to test 10s auto-modal conversion flow):
              </strong>
              <span style={{ fontSize: "11.5px", color: "#047857", fontWeight: 700 }}>
                20 Specialized Niches Live
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {MARKET_STRATEGY_DATA.demoStores20.map((site) => (
                <a
                  key={site.id}
                  href={`https://${site.demo}.ownezzy.com`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #86efac",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#065f46",
                    textDecoration: "none"
                  }}
                  title={`${site.name} — ${site.focus}`}
                >
                  {site.icon} {site.name} ({site.demo}) ↗
                </a>
              ))}
            </div>
          </div>

          {/* Master Linking Table */}
          <div className="linking-table-wrap">
            <table className="linking-table">
              <thead>
                <tr>
                  <th style={{ width: "18%" }}>Campaign Angle &amp; Hook</th>
                  <th style={{ width: "18%" }}>Target Audience</th>
                  <th style={{ width: "22%" }}>Primary Destination</th>
                  <th style={{ width: "24%" }}>Tracking Link with UTM</th>
                  <th style={{ width: "18%" }}>Conversion Trigger (Auto-Modal)</th>
                </tr>
              </thead>
              <tbody>
                {filteredLinkingMap.map((route) => {
                  const fullTrackingUrl = `${route.destUrl}${route.utm}`;
                  return (
                    <tr key={route.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                          {route.storeName && route.storeName !== "All Stores" ? (
                            <span className="script-badge-type" style={{ fontSize: "10.5px", padding: "1px 6px" }}>
                              🏪 {route.storeName}
                            </span>
                          ) : (
                            <span className="script-badge-type" style={{ fontSize: "10.5px", padding: "1px 6px", background: "#f1f5f9", color: "#475569", borderColor: "#cbd5e1" }}>
                              🌐 Universal
                            </span>
                          )}
                        </div>
                        <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>
                          {route.angle}
                        </strong>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                          "{route.hook}"
                        </span>
                      </td>

                      <td style={{ fontSize: "12px", color: "#334155" }}>
                        {route.target}
                      </td>

                      <td>
                        <a
                          href={route.destUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="link-destination"
                          title="Open Destination Storefront"
                        >
                          {route.destUrl.replace("https://", "")} ↗
                        </a>
                        <div style={{ fontSize: "10.5px", color: "#94a3b8", marginTop: "3px" }}>
                          Fallback: {route.fallback.replace("https://", "")}
                        </div>
                      </td>

                      <td>
                        <div className="link-tracking-url" title={fullTrackingUrl}>
                          {fullTrackingUrl}
                        </div>
                        <button
                          type="button"
                          className="btn-copy-utm"
                          onClick={() => copyToClipboard(fullTrackingUrl, `UTM tracking link for "${route.angle}" copied!`)}
                        >
                          📋 Copy Tracking Link
                        </button>
                      </td>

                      <td>
                        <span className="linking-pill">
                          ⚡ {route.modalTrigger}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* TAB 2: MULTILINGUAL VIDEO AD SCRIPTS */}
      {activeTab === "scripts" && (
        <section className="mkt-section-card" aria-label="Multilingual Scripts">
          <div className="mkt-section-header">
            <div>
              <h3 className="mkt-section-title">Multilingual Video Ad Scripts (9:16 Vertical Reels &amp; Shorts)</h3>
              <p className="mkt-section-desc">
                High-converting 25–30s scripts with exact visual actions, voiceovers in regional languages/tanglish, and high-CTR on-screen text overlays.
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <select
                value={selectedScriptType}
                onChange={(e) => setSelectedScriptType(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "12.5px" }}
              >
                <option value="all">All Scripts (Generic &amp; 20 Store Niches)</option>
                <option value="generic">Generic Scripts (All Sellers)</option>
                <option value="niche">All Niche Scripts (20 Stores)</option>
                <optgroup label="Filter by Specific Store Niche">
                  {MARKET_STRATEGY_DATA.demoStores20.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.icon} Store: {site.name} ({site.label})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          {/* Language Selector Strip */}
          <div className="lang-selector-strip">
            {MARKET_STRATEGY_DATA.languages.map((lang) => (
              <button
                key={lang.id}
                type="button"
                className={`lang-btn ${selectedLang === lang.id ? "active" : ""}`}
                onClick={() => setSelectedLang(lang.id)}
              >
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
                <small style={{ opacity: 0.8 }}>({lang.badge})</small>
              </button>
            ))}
          </div>

          {/* Script Cards */}
          <div>
            {activeScripts.map((script) => {
              const fullScriptText = `Title: ${script.title}\nTarget: ${script.target}\nDestination: ${script.destination}\nDuration: ${script.duration}\n\nSCENE BREAKDOWN:\n` +
                script.scenes.map((sc) => `[${sc.time}]\nVisual: ${sc.visual}\nVoiceover: ${sc.voiceover}\nOverlay: ${sc.textOverlay}`).join("\n\n");

              return (
                <div key={script.id} className="script-card">
                  <div className="script-header-row">
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span className="script-badge-type">
                          {script.type === "generic" ? "🌐 Generic Sellers" : `🎯 Niche: ${script.niche || "Boutique"}`}
                        </span>
                        <span style={{ fontSize: "12px", color: "#64748b" }}>⏱️ {script.duration}</span>
                      </div>
                      <h4 className="script-title">{script.title}</h4>
                      <p className="script-meta">
                        🎯 <strong>Target:</strong> {script.target} | 🔗 <strong>Destination:</strong> {script.destination}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="btn-copy-script"
                      onClick={() => copyToClipboard(fullScriptText, `Script "${script.title}" copied for video creator!`)}
                      title="Copy full script formatted for video creators and voiceover artists"
                    >
                      📋 Copy Full Script
                    </button>
                  </div>

                  <div className="scenes-table-wrap">
                    <table className="scenes-table">
                      <thead>
                        <tr>
                          <th style={{ width: "65px" }}>Time</th>
                          <th style={{ width: "32%" }}>Visual Action / Screen Recording</th>
                          <th style={{ width: "38%" }}>Voiceover (Spoken Words)</th>
                          <th style={{ width: "26%" }}>On-Screen Text Overlay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {script.scenes.map((scene, sIdx) => (
                          <tr key={sIdx}>
                            <td className="scene-time">{scene.time}</td>
                            <td>{scene.visual}</td>
                            <td>
                              <div style={{ fontWeight: 600, color: "#0f172a" }}>{scene.voiceover}</div>
                              {scene.voiceoverLocal && (
                                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                                  {scene.voiceoverLocal}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className="scene-overlay">{scene.textOverlay}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* TAB 3: SIMULATOR & OPTIMIZATION TOOLS */}
      {activeTab === "simulator" && (
        <section className="mkt-section-card" aria-label="Meta Ads Simulator">
          <div className="mkt-section-header">
            <div>
              <h3 className="mkt-section-title">Optimization Tools: Meta Ads Budget &amp; ROAS Calculator</h3>
              <p className="mkt-section-desc">
                Simulate daily advertising spend, projected reach, demo store visits, 10s modal triggers, free trial signups, and paid subscriber MRR.
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-copy-script"
                onClick={() => {
                  setSimDailyBudget(333);
                  setSimNiche("blouses");
                  setSimGeo("south");
                  notify("Loaded ₹10k/month plan (~₹333/day) preset into simulator!", "success");
                }}
              >
                ⚡ Load ₹10k Plan Preset (₹333/day)
              </button>
              <span style={{ fontSize: "12px", background: "#f0fdf4", color: "#166534", padding: "4px 10px", borderRadius: "6px", fontWeight: 700 }}>
                Live Benchmark: 18.4% Trial-to-Paid Conversion
              </span>
            </div>
          </div>

          <div className="simulator-grid">
            {/* Left: Input controls */}
            <div className="sim-input-box">
              <div className="sim-field">
                <label>
                  <span>Daily Ad Spend:</span>
                  <strong style={{ fontSize: "15px", color: "#10b981" }}>₹{simDailyBudget.toLocaleString("en-IN")} / day</strong>
                </label>
                <input
                  type="range"
                  min={300}
                  max={25000}
                  step={simDailyBudget <= 1000 ? 50 : 500}
                  value={simDailyBudget}
                  onChange={(e) => setSimDailyBudget(parseInt(e.target.value, 10))}
                />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#94a3b8" }}>
                  <span>₹300/day</span>
                  <span>₹10,000/day</span>
                  <span>₹25,000/day</span>
                </div>
              </div>

              <div className="sim-field">
                <label>Target Niche Campaign:</label>
                <select value={simNiche} onChange={(e) => setSimNiche(e.target.value)}>
                  <option value="generic">🌐 Generic (All Home &amp; Micro Sellers) — CTR 3.1%</option>
                  <optgroup label="All 20 Specialized Demo Store Niches">
                    {MARKET_STRATEGY_DATA.demoStores20.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.icon} {site.name} ({site.label}) — CTR {(site.ctr * 100).toFixed(1)}%
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className="sim-field">
                <label>Target Geography Preset:</label>
                <select value={simGeo} onChange={(e) => setSimGeo(e.target.value)}>
                  <option value="south">South India (Chennai, Coimbatore, Bangalore, Hyderabad, Kochi)</option>
                  <option value="metro">Tier-1 Metros (Delhi, Mumbai, Bangalore, Pune)</option>
                  <option value="panindia">Pan-India Tier-2 &amp; Tier-3 Industrial Towns</option>
                </select>
              </div>

              <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "10px 12px" }}>
                <span style={{ fontSize: "11.5px", color: "#475569" }}>
                  💡 <strong>Ad Placement Recommendation:</strong> Meta Ads Advantage+ overridden strictly to <strong>Instagram Reels (90%) and Stories (10%)</strong> on mobile devices only.
                </span>
              </div>
            </div>

            {/* Right: Projected output */}
            <div className="sim-output-box">
              <div className="sim-output-header">
                📊 Projected Performance (Monthly Ad Spend: ₹{simResults.monthlyBudget.toLocaleString("en-IN")})
              </div>

              <div className="sim-metric-row">
                <span>Daily Video Impressions:</span>
                <strong>~{simResults.dailyImpressions.toLocaleString("en-IN")} views/day</strong>
              </div>

              <div className="sim-metric-row">
                <span>Daily Clicks to Demo Store:</span>
                <strong>~{simResults.dailyClicks} clicks (Avg CPC: ₹{simResults.avgCpc})</strong>
              </div>

              <div className="sim-metric-row">
                <span>Daily 7-Day Free Trials:</span>
                <strong>~{simResults.dailyTrials} store signups / day</strong>
              </div>

              <div className="sim-metric-row">
                <span>Monthly Free Trials Created:</span>
                <strong>{simResults.monthlyTrials} new stores</strong>
              </div>

              <div className="sim-metric-row" style={{ borderTop: "1px dashed #bbf7d0", paddingTop: "8px" }}>
                <span>Converted Paid Subscribers (18.4%):</span>
                <strong style={{ fontSize: "16px", color: "#065f46" }}>~{simResults.monthlyPaidStores} paid stores / month</strong>
              </div>

              <div className="sim-metric-row">
                <span>Projected CAC (Customer Acquisition Cost):</span>
                <strong style={{ color: "#166534" }}>₹{simResults.estimatedCac} / store</strong>
              </div>

              <div className="sim-metric-row">
                <span>New Monthly Recurring Revenue (MRR):</span>
                <strong style={{ fontSize: "16px", color: "#059669" }}>+₹{simResults.projectedMrr.toLocaleString("en-IN")} / mo</strong>
              </div>

              <div className="sim-metric-row" style={{ background: "#ffffff", padding: "6px 10px", borderRadius: "6px" }}>
                <span>Estimated 6-Month Blended ROAS:</span>
                <strong style={{ fontSize: "17px", color: "#047857" }}>{simResults.roas}x ROAS</strong>
              </div>
            </div>
          </div>

          {/* AI Ad Hook Generator */}
          <div style={{ marginTop: "14px" }}>
            <h4 style={{ margin: "0 0 10px", fontSize: "15px", fontWeight: 800 }}>
              ⚡ AI Creative &amp; Ad Hook Generator
            </h4>
            <div className="ai-copy-box">
              <div className="ai-copy-controls">
                <select value={aiPlatform} onChange={(e) => setAiPlatform(e.target.value)}>
                  <option value="reels">Instagram Reels Ad</option>
                  <option value="story">Meta Story Ad</option>
                  <option value="whatsapp">WhatsApp Broadcast Ad</option>
                  <option value="feed">Facebook Feed Ad</option>
                </select>

                <select value={aiHookType} onChange={(e) => setAiHookType(e.target.value)}>
                  <option value="pain">Pain Killer: Tired of "Price Please" DMs</option>
                  <option value="zeroComm">Financial Gain: 0% Sales Commission &amp; Direct UPI</option>
                  <option value="fastReady">Speed &amp; Convenience: Store Live in 60s Without Coding</option>
                </select>

                <select value={aiLang} onChange={(e) => setAiLang(e.target.value)}>
                  <option value="en">English (Universal)</option>
                  <option value="hi">Hindi (हिंदी / Hinglish)</option>
                  <option value="ta">Tamil (தமிழ் / Tanglish)</option>
                </select>

                <button
                  type="button"
                  className="btn-reanalyze-mkt"
                  style={{ padding: "6px 14px", fontSize: "12px" }}
                  onClick={generateAdHook}
                >
                  ✨ Refresh Hook
                </button>
              </div>

              <div className="ai-copy-output">
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "13px" }}>
                  {generatedHook}
                </pre>
                <div style={{ marginTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn-copy-script"
                    onClick={() => copyToClipboard(generatedHook, "Ad hook & caption copied!")}
                  >
                    📋 Copy Caption &amp; Hashtags
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Niche Redirection & Demo Store Checker */}
          <div style={{ marginTop: "14px" }}>
            <h4 style={{ margin: "0 0 8px", fontSize: "15px", fontWeight: 800 }}>
              🔗 Niche Demo Stores &amp; 10-Second Auto-Modal Flow
            </h4>
            <p style={{ margin: "0 0 10px", fontSize: "12.5px", color: "#64748b" }}>
              Directing ad clicks straight to these tailored demo storefronts increases signup rates by 3x compared to a generic homepage:
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "10px" }}>
              {MARKET_STRATEGY_DATA.demoStores20.map((site) => (
                <div key={site.id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", background: "#ffffff" }}>
                  <strong style={{ fontSize: "13px", display: "block" }}>{site.icon} {site.name}</strong>
                  <span style={{ fontSize: "11px", color: "#64748b" }}>{site.label}</span>
                  <div style={{ marginTop: "6px" }}>
                    <a
                      href={`https://${site.demo}.ownezzy.com`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: "12px", color: "#10b981", fontWeight: 700, textDecoration: "none" }}
                    >
                      Test {site.demo}.ownezzy.com ↗
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TAB 4: RECOMMENDATIONS & ACTION CHECKLIST */}
      {activeTab === "recommendations" && (
        <section className="mkt-section-card" aria-label="Marketing Recommendations">
          <div className="mkt-section-header">
            <div>
              <h3 className="mkt-section-title">AI Marketing Recommendations &amp; Execution Checklist</h3>
              <p className="mkt-section-desc">
                Track critical milestones and growth recommendations. Check off completed actions to update marketing readiness score.
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#065f46" }}>
                Progress: {checklistStats.completed} of {checklistStats.total} ({checklistStats.pct}%)
              </span>
              <div style={{ width: "100px", height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                <div style={{ width: `${checklistStats.pct}%`, height: "100%", background: "#10b981", transition: "width 0.3s" }}></div>
              </div>
            </div>
          </div>

          <div className="checklist-container">
            {MARKET_STRATEGY_DATA.actionChecklist.map((item) => {
              const isDone = checklist[item.id] !== undefined ? checklist[item.id] : item.completed;
              return (
                <div key={item.id} className={`checklist-item ${isDone ? "done" : ""}`}>
                  <input
                    type="checkbox"
                    className="checklist-checkbox"
                    checked={isDone}
                    onChange={() => handleToggleChecklist(item.id, isDone)}
                  />
                  <div className="checklist-content">
                    <div className="checklist-title-row">
                      <span className={`checklist-priority-badge ${item.priority}`}>{item.priority}</span>
                      <strong style={{ fontSize: "13.5px", color: isDone ? "#166534" : "#0f172a" }}>
                        {item.title}
                      </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: "12.5px", color: "#64748b" }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* TAB 5: AUTOMATED WHATSAPP CADENCE & BOT */}
      {activeTab === "cadence" && (
        <section className="mkt-section-card" aria-label="WhatsApp Sales & Automation">
          <div className="mkt-section-header">
            <div>
              <h3 className="mkt-section-title">⚡ WhatsApp Business Automated Replies &amp; Sales Cadence</h3>
              <p className="mkt-section-desc">
                Configure automated inbound replies with live demo links, 24/7 query triage, Quick-Reply shortcuts, and outbound 7-day trial conversion cadence.
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                type="button"
                className={`btn-copy-script ${cadenceSubTab === "autoReplies" ? "active" : ""}`}
                style={cadenceSubTab === "autoReplies" ? { background: "#065f46", color: "#ffffff", borderColor: "#065f46" } : {}}
                onClick={() => setCadenceSubTab("autoReplies")}
              >
                🤖 Inbound Auto-Replies &amp; Bot
              </button>
              <button
                type="button"
                className={`btn-copy-script ${cadenceSubTab === "onboarding" ? "active" : ""}`}
                style={cadenceSubTab === "onboarding" ? { background: "#065f46", color: "#ffffff", borderColor: "#065f46" } : {}}
                onClick={() => setCadenceSubTab("onboarding")}
              >
                🚀 Outbound 14-Day Nurture
              </button>
            </div>
          </div>

          {/* SUB-VIEW 1: INBOUND AUTO-REPLIES & CHATBOT SETUP */}
          {cadenceSubTab === "autoReplies" && (
            <div>
              {/* Dual Setup Instructions Banner */}
              <div className="wa-setup-grid">
                {/* Method 1: Mobile App Setup */}
                <div className="wa-setup-card phone-app">
                  <div className="wa-setup-header">
                    <span className="wa-setup-title">
                      📱 Method 1: WhatsApp Business Phone App
                    </span>
                    <span className="wa-setup-badge green">
                      0-Code / 2 Minutes Setup
                    </span>
                  </div>
                  <p style={{ fontSize: "12px", color: "#475569", margin: "0 0 12px" }}>
                    Configure the official WhatsApp Business app on your smartphone to instantly greet prospects and respond with macros.
                  </p>
                  <div className="wa-steps-list">
                    {MARKET_STRATEGY_DATA.whatsappAutoReplies?.mobileAppGuide?.map((item, idx) => (
                      <div key={idx} className="wa-step-item">
                        <span className="wa-step-num">{item.step}</span>
                        <div className="wa-step-content">
                          <strong>{item.title}</strong>
                          <div>{item.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Method 2: Server Webhook / Cloud API */}
                <div className="wa-setup-card webhook-api">
                  <div className="wa-setup-header">
                    <span className="wa-setup-title">
                      🤖 Method 2: Meta Cloud API Server Webhook
                    </span>
                    <span className="wa-setup-badge blue">
                      ⚡ Active &amp; Autonomous
                    </span>
                  </div>
                  <p style={{ fontSize: "12px", color: "#475569", margin: "0 0 10px" }}>
                    Backend webhook handler running 24/7 on Ownezzy server to parse inbound numbers/keywords and dispatch immediate replies.
                  </p>
                  <div style={{ fontSize: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div>
                      <strong style={{ color: "#0f172a" }}>Webhook Callback URL:</strong>
                      <code className="wa-code-snippet">
                        {MARKET_STRATEGY_DATA.whatsappAutoReplies?.cloudApiWebhookGuide?.endpoint}
                      </code>
                    </div>
                    <div>
                      <strong style={{ color: "#0f172a" }}>Subscribed Webhook Fields:</strong>
                      <span style={{ marginLeft: "6px", color: "#2563eb", fontWeight: 700 }}>
                        messages, message_deliveries
                      </span>
                    </div>
                    <div>
                      <strong style={{ color: "#0f172a" }}>Keyword Parser:</strong>
                      <span style={{ marginLeft: "6px", color: "#475569" }}>
                        Numbers 1–5, "demo", "price", "launch", "custom", "support", "blouse", "case"
                      </span>
                    </div>
                    <div>
                      <strong style={{ color: "#0f172a" }}>Verification:</strong>
                      <span style={{ marginLeft: "6px", color: "#059669", fontWeight: 600 }}>
                        HMAC-SHA256 signature check &amp; GET challenge verification
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick-Replies Shortcuts Table */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <strong style={{ fontSize: "14px", color: "#0f172a" }}>
                    ⚡ 1-Tap Quick-Reply Keyboard Shortcuts (Type "/" in WhatsApp Chat)
                  </strong>
                  <span style={{ fontSize: "11.5px", color: "#64748b" }}>
                    Saves 4 hours/day for support executives
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                    <thead>
                      <tr style={{ background: "#edf2f7", textAlign: "left" }}>
                        <th style={{ padding: "8px 10px", borderBottom: "1px solid #cbd5e1" }}>Shortcut</th>
                        <th style={{ padding: "8px 10px", borderBottom: "1px solid #cbd5e1" }}>Purpose &amp; Content</th>
                        <th style={{ padding: "8px 10px", borderBottom: "1px solid #cbd5e1" }}>Best Used When</th>
                        <th style={{ padding: "8px 10px", borderBottom: "1px solid #cbd5e1", textAlign: "right" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MARKET_STRATEGY_DATA.whatsappAutoReplies?.quickReplyShortcuts?.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "8px 10px" }}>
                            <span className="wa-shortcut-badge">{row.shortcut}</span>
                          </td>
                          <td style={{ padding: "8px 10px", color: "#1e293b", fontWeight: 600 }}>
                            {row.purpose}
                          </td>
                          <td style={{ padding: "8px 10px", color: "#64748b" }}>
                            {row.trigger}
                          </td>
                          <td style={{ padding: "8px 10px", textAlign: "right" }}>
                            <button
                              type="button"
                              className="btn-copy-utm"
                              onClick={() => {
                                const matched = MARKET_STRATEGY_DATA.whatsappAutoReplies?.scripts?.find(s => s.shortcut === row.shortcut);
                                if (matched) {
                                  copyToClipboard(matched.text, `Template for ${row.shortcut} copied!`);
                                } else {
                                  copyToClipboard(row.shortcut, `Shortcut ${row.shortcut} copied!`);
                                }
                              }}
                            >
                              📋 Copy Text
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ready-to-Copy Auto-Replies */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
                  <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 800 }}>
                    📋 Ready-to-Copy WhatsApp Auto-Replies &amp; Interactive Menus
                  </h4>
                  <div className="wa-filter-tabs">
                    {["all", "Greeting & Menu", "Demo Links", "Pricing & Offer", "Store Creation", "Custom Store", "Human Escalation", "Niche Reply"].map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        className={`wa-filter-btn ${waScriptCategory === cat ? "active" : ""}`}
                        onClick={() => setWaScriptCategory(cat)}
                      >
                        {cat === "all" ? "All Templates" : cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wa-scripts-grid">
                  {MARKET_STRATEGY_DATA.whatsappAutoReplies?.scripts
                    ?.filter((item) => waScriptCategory === "all" || item.category === waScriptCategory)
                    ?.map((item) => (
                      <div key={item.id} className="wa-script-card">
                        <div className="wa-script-header">
                          <div>
                            <span className="wa-shortcut-badge" style={{ marginRight: "8px" }}>
                              {item.shortcut}
                            </span>
                            <span className="wa-script-name">{item.name}</span>
                          </div>

                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              className="btn-copy-script"
                              onClick={() => copyToClipboard(item.text, `Auto-reply template "${item.shortcut}" copied!`)}
                            >
                              📋 Copy Message
                            </button>
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(item.text)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-copy-script"
                              style={{ background: "#25D366", color: "#ffffff", borderColor: "#22c55e", textDecoration: "none" }}
                            >
                              📲 Test in WhatsApp
                            </a>
                          </div>
                        </div>

                        <div className="wa-script-trigger">
                          ⚡ <strong>Trigger:</strong> {item.trigger}
                        </div>

                        <div className="cadence-template-box" style={{ background: "#ffffff", borderColor: "#cbd5e1" }}>
                          {item.text}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* SUB-VIEW 2: OUTBOUND 7-DAY TRIAL NURTURE CADENCE */}
          {cadenceSubTab === "onboarding" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 800 }}>
                    🚀 7-Day Free Trial to Paid Subscription Sequence
                  </h4>
                  <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>
                    Automated outbound triggers to convert trial merchants into long-term ₹399/mo paying shops.
                  </p>
                </div>
                <span style={{ fontSize: "12px", background: "#eff6ff", color: "#1d4ed8", padding: "4px 10px", borderRadius: "6px", fontWeight: 700 }}>
                  Conversion Lift: +40% with WhatsApp Cadence
                </span>
              </div>

              <div className="cadence-timeline">
                {MARKET_STRATEGY_DATA.salesCadence.map((cadence, idx) => (
                  <div key={idx} className="cadence-card">
                    <div className="cadence-header">
                      <div>
                        <span className="cadence-stage-badge">{cadence.stage}</span>
                        <strong style={{ marginLeft: "8px", fontSize: "14.5px", color: "#0f172a" }}>
                          {cadence.title}
                        </strong>
                      </div>

                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          type="button"
                          className="btn-copy-script"
                          onClick={() => copyToClipboard(cadence.template, `WhatsApp template for ${cadence.stage} copied!`)}
                        >
                          📋 Copy Template
                        </button>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(cadence.template)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-copy-script"
                          style={{ background: "#25D366", color: "#ffffff", borderColor: "#22c55e", textDecoration: "none" }}
                        >
                          📲 Test on WhatsApp Web
                        </a>
                      </div>
                    </div>

                    <div style={{ fontSize: "11.5px", color: "#64748b", marginBottom: "8px" }}>
                      ⚡ <strong>Trigger:</strong> {cadence.trigger}
                    </div>

                    <div className="cadence-template-box">
                      {cadence.template}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* TAB 6: OBJECTION HANDLING */}
      {activeTab === "objections" && (
        <section className="mkt-section-card" aria-label="Objection Handling">
          <div className="mkt-section-header">
            <div>
              <h3 className="mkt-section-title">Winning Sales Responses to Common Seller Objections</h3>
              <p className="mkt-section-desc">
                Field-tested scripts for support executives and automated chatbots to resolve merchant doubts and close stores quickly.
              </p>
            </div>
          </div>

          <div className="objections-grid">
            {MARKET_STRATEGY_DATA.objections.map((item, idx) => (
              <div key={idx} className="objection-card">
                <div className="objection-q">❓ "{item.objection}"</div>
                <div className="objection-a">💬 {item.response}</div>
                <div style={{ marginTop: "10px", textAlign: "right" }}>
                  <button
                    type="button"
                    className="btn-copy-script"
                    style={{ fontSize: "11px", padding: "3px 8px" }}
                    onClick={() => copyToClipboard(item.response, "Response copied!")}
                  >
                    📋 Copy Answer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
